import { ArrowLeftIcon, Button, LockIcon } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BlockPhase } from '../../../pages/Timetable/Plan/TimetableVersion/TimetableVersionLayout';
import { ModuleLicenseLock } from '../../LicenseHint/ModuleLicenseLock';
import { NavigationTabs } from '../../NavigationTabs/NavigationTabs';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import styles from './TimetableVersionTopBar.module.scss';

export const TimetableVersionTopBar = observer(
  ({
    versionUuid,
    baseSlug,
    blockPhase = 'running',
    onStartFix,
    onCompleteFix,
  }: {
    versionUuid: string;
    baseSlug: string;
    blockPhase?: BlockPhase;
    onStartFix?: () => Promise<void>;
    onCompleteFix?: () => Promise<boolean>;
  }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const pinboardStore = useTimetableStore();

    const isOnData = location.pathname.includes(t('routes.versionPages.data.slug'));

    // Merkt sich die zuletzt geöffnete Stundenplandaten-Unterseite (z. B. Lehrer,
    // Räume …), damit sie beim Wechsel Stecktafel -> Stundenplandaten erhalten bleibt.
    // Die TopBar bleibt über den Wechsel gemountet, daher genügt eine Ref.
    const lastDataPathRef = useRef<string | null>(null);
    if (isOnData) {
      lastDataPathRef.current = location.pathname;
    }
    const dataTabPath =
      lastDataPathRef.current ??
      `/${baseSlug}/${t('routes.versions.slug')}/${versionUuid}/${t('routes.versionPages.data.slug')}/${t('routes.versionsFilter.class.slug')}`;

    return (
      <div className={styles['timetable-version-top-bar']}>
        <div className={styles.header}>
          <Button
            className={styles.back}
            icon={<ArrowLeftIcon />}
            hierarchy='tertiary'
            onClick={() => navigate('/' + t('routes.timetable.slug') + '/' + t('routes.plan.slug'), { replace: true })}
          />
          <div
            className={styles.headline}
          >{`${pinboardStore.currentVersion?.timetableName} - ${t('timetableVersion.title', { count: 1 })} ${pinboardStore.currentVersion?.versionName}`}</div>
          <ModuleLicenseLock />
          {pinboardStore.currentVersion?.version && (
            <div className={styles.description}>({pinboardStore.currentVersion.version})</div>
          )}
          {pinboardStore.currentVersion?.description && (
            <div className={styles.description}>
              <p>({pinboardStore.currentVersion.description})</p>
            </div>
          )}
          {(onStartFix || onCompleteFix) &&
            pinboardStore.currentVersion?.active &&
            blockPhase !== null &&
            // Entwurf mit künftigem Abschnitt: Termine erstellen. Ein veröffentlichter Stundenplan wird in jeder Phase
            // über „Version korrigieren“ bearbeitet (Aktivieren sperrt die Version), auch wenn heute kein Abschnitt läuft.
            (blockPhase === 'future' && !pinboardStore.timetableActive ? (
              <Button hierarchy='secondary' onClick={() => onCompleteFix?.()}>
                {t('timetableVersion.createEvents')}
              </Button>
            ) : pinboardStore.temporarilyEditable ? (
              <Button
                icon={<LockIcon className='small' />}
                hierarchy='primary'
                onClick={async () => {
                  // Edit-Status nur zurücksetzen, wenn die Aktion durchlief (z.B. Sperren nicht gescheitert ist).
                  if (await onCompleteFix?.()) {
                    pinboardStore.setTemporarilyEditable(false);
                  }
                }}
              >
                {blockPhase === 'past'
                  ? t('timetableVersion.completeFix')
                  : t('timetableVersion.completeFixAndCreateEvents')}
              </Button>
            ) : (
              <Button
                hierarchy='secondary'
                onClick={async () => {
                  pinboardStore.setTemporarilyEditable(true);
                  try {
                    await onStartFix?.();
                    // Duplicate/Snapshot ist durch -> TimetableVersionData neu laden, damit der
                    // jetzt befüllte `editor` in den Datentabellen ankommt.
                    pinboardStore.reloadVersionData();
                  } catch {
                    // Fix fehlgeschlagen -> temporären Edit-Status zurücksetzen.
                    pinboardStore.setTemporarilyEditable(false);
                  }
                }}
              >
                {t('timetableVersion.fixVersion')}
              </Button>
            ))}
        </div>

        <NavigationTabs
          className={styles['tab-bar']}
          tabs={[
            {
              title: t('timetable.timetableData'),
              path: dataTabPath,
              active: isOnData,
            },
            {
              title: t('pinboard.title', { count: 1 }),
              path: `/${baseSlug}/${t('routes.versions.slug')}/${versionUuid}/${t('routes.versionPages.board.slug')}`,
            },
          ]}
        />
      </div>
    );
  },
);
